/**
 * WorkforceRouteGuard — Prompt 10A §6-§8.
 *
 * Frontend UX guard for the /workforce routes. Renders the outlet only
 * when ALL of the following hold; otherwise redirects to "/" (the
 * existing HomeRedirect then routes the user to the correct MiseOne
 * landing destination).
 *
 *   1. user.role is one of the four authorized Workforce admin roles
 *      (restaurant, restaurant_admin, corporate, super_admin);
 *   2. resolved modules.workforce is exactly "BETA" or "ON"
 *      (missing/invalid ≡ OFF);
 *   3. an active restaurantId exists in AppContext.
 *
 * Backend remains authoritative — this guard is UX only. It never
 * calls the backend directly; it consumes already-resolved
 * ResolvedConfigContext + AppContext values.
 */
import { Navigate, Outlet } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { useResolvedConfig } from "@/context/ResolvedConfigContext";

export const WORKFORCE_ADMIN_ROLES = Object.freeze([
    "restaurant", "restaurant_admin", "corporate", "super_admin",
]);

export function isWorkforceActive(config) {
    const state = config?.modules?.workforce;
    return state === "BETA" || state === "ON";
}

export function canAccessWorkforce({ user, restaurantId, config }) {
    if (!user || !WORKFORCE_ADMIN_ROLES.includes(user.role)) return false;
    if (!restaurantId) return false;
    return isWorkforceActive(config);
}

export default function WorkforceRouteGuard() {
    const { user, restaurantId } = useApp();
    const { config } = useResolvedConfig();
    if (!canAccessWorkforce({ user, restaurantId, config })) {
        return <Navigate to="/" replace />;
    }
    return <Outlet />;
}
